import { postingSources } from './opportunitySources';
import { locationLabel } from './locationLabel';
import { normalizeWorkModel } from './workArrangement';
export { normalizeWorkModel } from './workArrangement';

export const ASSESSMENT_ORDER = { Priority: 0, Conditional: 1, Hold: 2, Reach: 2.5, "Needs review": 3, Excluded: 4 };
export const LABEL_FIELDS = [
  ["assessment", "Assessment"], ["responsibility_family", "Responsibility"],
  ["seniority", "Level"], ["work_model", "Work arrangement"],
  ["travel_label", "Travel + %"],
  ["location", "Location"], ["employment_type", "Engagement"], ["status", "Workflow"],
  ["source", "Source"],
];
export const REGIONAL_AREAS = {
  "Area: GTA (Greater Toronto Area)": [
    "Toronto", "Etobicoke", "North York", "Scarborough", "Mississauga", "Brampton",
    "Caledon", "Oakville", "Burlington", "Milton", "Halton Hills", "Markham",
    "Richmond Hill", "Vaughan", "Aurora", "Newmarket", "Pickering", "Ajax", "Whitby", "Oshawa"
  ],
  "Area: Halton Region": [
    "Oakville", "Burlington", "Milton", "Halton Hills"
  ],
  "Area: Peel Region": [
    "Mississauga", "Brampton", "Caledon"
  ],
  "Area: Waterloo Region (Tri-Cities)": [
    "Kitchener", "Waterloo", "Cambridge"
  ],
  "Area: York Region": [
    "Markham", "Richmond Hill", "Vaughan", "Aurora", "Newmarket"
  ],
  "Area: Hamilton Area": [
    "Hamilton", "Stoney Creek", "Ancaster"
  ]
};

export function labelValue(job, key) {
  if (key === 'location') {
    return locationLabel(job.location, job.raw?.location_review, job.source_url).display || 'City omitted';
  }
  if (key === "responsibility_family") {
    return String(job.review?.responsibility_family || job.responsibility_family || "Unknown");
  }
  if (key === "work_model") {
    return normalizeWorkModel(job.work_model);
  }
  return String(job[key] || "Unknown");
}

export function availableLabels(jobs) {
  return Object.fromEntries(LABEL_FIELDS.map(([key]) => {
    if (key === 'location') {
      const cities = new Map();
      jobs.forEach(job => {
        const city = labelValue(job, key);
        if (city !== 'City omitted' && !cities.has(city.toLocaleLowerCase())) cities.set(city.toLocaleLowerCase(), city);
      });
      return [key, [...cities.values()].sort((a, b) => a.localeCompare(b))];
    }
    const rawValues = [...new Set(jobs.filter(job => key !== 'travel_label' || hasTravelLabel(job)).flatMap(job => key === 'source' ? postingSources(job).map(source => source.label) : [labelValue(job, key)]))].sort();
    return [key, rawValues];
  }));
}

export function hasTravelLabel(job) {
  const label = String(job.travel_label || '').trim();
  return Boolean(label) && !/^(?:travel\s*[·:—-]?\s*)?(?:not stated|not mentioned|unknown|none|no travel|0%)$/i.test(label);
}

export function matchesLocation(jobLocation, selectedLocations) {
  if (!selectedLocations || !selectedLocations.length) return true;
  const key = value => (String(value || '').includes(' — ') ? String(value) : locationLabel(value).display).trim().toLocaleLowerCase();
  const city = key(jobLocation);
  return selectedLocations.some(sel => city && city === key(sel));
}

export function locationOptionCounts(jobs, search = '', selected = {}) {
  const { location: ignored, ...otherFilters } = selected;
  const counts = {}, seen = new Set();
  for (const job of jobs) {
    const id = job.id == null ? job : String(job.id);
    if (seen.has(id)) continue;
    seen.add(id);
    if (search && !`${job.title} ${job.company_name} ${job.location}`.toLowerCase().includes(search.toLowerCase())) continue;
    if (!matchesLabels(job, otherFilters)) continue;
    const city = labelValue(job, 'location');
    if (!city || city === 'City omitted') continue;
    const key = city.toLocaleLowerCase();
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

export function matchesLabels(job, selected) {
  return Object.entries(selected).every(([field, values]) => {
    if (!values || !values.length) return true;
    if (field === 'source') {
      return postingSources(job).some(source => values.includes(source.label));
    }
    if (field === 'location') {
      return matchesLocation(labelValue(job, 'location'), values);
    }
    return values.includes(labelValue(job, field));
  });
}
export function compareReview(a, b) {
  return (ASSESSMENT_ORDER[a.assessment] ?? 3) - (ASSESSMENT_ORDER[b.assessment] ?? 3)
    || (a.review_rank ?? Number.MAX_SAFE_INTEGER) - (b.review_rank ?? Number.MAX_SAFE_INTEGER)
    || a.title.localeCompare(b.title) || String(a.id).localeCompare(String(b.id));
}
export function compareMatch(a, b) {
  return (b.match_score ?? -1) - (a.match_score ?? -1)
    || (a.match_rank ?? Number.MAX_SAFE_INTEGER) - (b.match_rank ?? Number.MAX_SAFE_INTEGER) || compareReview(a, b);
}
export function compareOrdered(a, b, order) {
  const nullable = (x, y, descending = false) => {
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return descending ? y - x : x - y;
  };
  const date = job => {
    const value = Date.parse(job.posted_at || job.created_at || '');
    return Number.isFinite(value) ? value : null;
  };
  for (const key of order) {
    let result = 0;
    if (key === 'score') result = nullable(a.match_score, b.match_score, true);
    if (key === 'hiring') result = nullable(a.hiring_rank, b.hiring_rank);
    if (key === 'review') result = (ASSESSMENT_ORDER[a.assessment] ?? 3) - (ASSESSMENT_ORDER[b.assessment] ?? 3)
      || nullable(a.review_rank, b.review_rank);
    if (key === 'newest' || key === 'oldest') result = nullable(date(a), date(b), key === 'newest');
    if (result) return result;
  }
  return compareMatch(a, b);
}
export function sortDocumentsByMatch(documents, jobs) {
  const orderedJobs = [...jobs].sort(compareMatch);
  const positions = new Map(orderedJobs.map((job, index) => [String(job.id), index]));
  return [...documents].sort((a, b) =>
    (positions.get(String(a.job_id)) ?? Number.MAX_SAFE_INTEGER)
    - (positions.get(String(b.job_id)) ?? Number.MAX_SAFE_INTEGER));
}
export function dateLabel(job, now = new Date()) {
  const posted = job.posted_at && !Number.isNaN(Date.parse(job.posted_at));
  const value = posted ? job.posted_at : job.created_at;
  if (!value || Number.isNaN(Date.parse(value))) return null;
  const days = Math.floor((now - new Date(value)) / 86400000);
  const prefix = posted ? "Posted" : "Added";
  if (days < 0) return `${prefix} ${new Date(value).toLocaleDateString()}`;
  return `${prefix} ${days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`}`;
}

export function getOpportunitySummary(job) {
  if (!job) return "";
  const fidelity = job.raw?.discovery_fidelity;
  if (fidelity && fidelity.status !== 'verified_posting') {
    const label = fidelity.status === 'verified_program' ? 'Verified program intake — specific project and eligibility pending.'
      : fidelity.status === 'unsupported_target' ? 'Historical employer target — no exact vacancy verified.'
      : fidelity.status === 'restricted' ? 'Source access restricted — current availability unverified.'
      : job.status === 'archived' ? 'Archived posting — current availability not established.'
      : 'Source verification pending — current availability not established.';
    return `${label} ${fidelity.reason || ''}`.trim();
  }
  const desc = job.description || job.description_snapshot || "";
  const clean = desc.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");
  const lines = clean.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  const skipPrefixes = [
    "skip to", "back to", "job application for", "job id:", "apply", "page_title",
    "home", "department", "view profile", "employee login", "show more", "loading...",
    "tags.", "search by", "we use cookies", "save job", "share job", "select how often",
    "posting date:", "live status:", "source:"
  ];

  const cleanLines = [];
  const lowerTitle = (job.title || "").toLowerCase();
  const lowerCompany = (job.company_name || "").toLowerCase();

  for (const l of lines) {
    const lower = l.toLowerCase();
    if (skipPrefixes.some(p => lower.startsWith(p))) continue;
    if ([
      "description", "about the job", "about the role", "the opportunity",
      "position summary", "overview", "summary", "about us", "about us:",
      "key job responsibilities", "responsibilities"
    ].includes(lower)) continue;
    if (lower === lowerTitle || lower === lowerCompany) continue;
    if (/job id:\s*\d+/i.test(lower)) continue;
    if (l.length > 35) {
      cleanLines.push(l);
    }
  }

  if (cleanLines.length > 0) {
    return cleanLines.slice(0, 6).join(" ").replace(/\s+/g, " ").trim();
  }
  return desc.slice(0, 450).trim();
}

